import { useEffect, useRef, useState } from 'react';
import { useInView, useReducedMotion } from 'framer-motion';

/**
 * 자동 재생되는 스크립트형 채팅 데모 엔진.
 * - 실시간 LLM 백엔드 없이 시나리오(script)를 순차 재생(스트리밍·툴콜 배지).
 * - 화면에 보일 때만 재생(framer-motion useInView), 벗어나면 정지.
 * - prefers-reduced-motion 이면 애니메이션 없이 최종 대화 전체를 정적 표시.
 *
 * variant:
 *  - 'qa'    : 질문 → 도구 호출 → 답변 (MCP)
 *  - 'trace' : 계획·관찰 노트를 포함한 에이전트 트레이스 (AI Agent)
 */

export type ChatStep =
  | { kind: 'user'; text: string }
  | { kind: 'tools'; tools: string[] }
  | { kind: 'note'; label: string; text: string }
  | { kind: 'bot'; text: string; chips?: string[] };

interface RenderItem {
  kind: ChatStep['kind'];
  text?: string;
  shown?: string;
  tools?: string[];
  toolsDone?: boolean;
  label?: string;
  chips?: string[];
  typing?: boolean;
}

interface ChatDemoProps {
  script: ChatStep[];
  title?: string;
  variant?: 'qa' | 'trace';
  caption?: string;
}

const wait = (ms: number, timers: ReturnType<typeof setTimeout>[]) =>
  new Promise<void>((res) => {
    timers.push(setTimeout(res, ms));
  });

/** reduced-motion 정적 표시용: 모든 스텝을 완성 상태로 변환 */
function fullState(script: ChatStep[]): RenderItem[] {
  return script.map((s) => {
    if (s.kind === 'bot') return { kind: 'bot', text: s.text, shown: s.text, chips: s.chips };
    if (s.kind === 'tools') return { kind: 'tools', tools: s.tools, toolsDone: true };
    if (s.kind === 'note') return { kind: 'note', label: s.label, text: s.text, shown: s.text };
    return { kind: 'user', text: s.text, shown: s.text };
  });
}

export default function ChatDemo({ script, title = 'Live Demo', variant = 'qa', caption }: ChatDemoProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { amount: 0.4 });
  const reduce = useReducedMotion();
  const active = inView && !reduce;

  const [items, setItems] = useState<RenderItem[]>(reduce ? fullState(script) : []);

  useEffect(() => {
    if (reduce) {
      setItems(fullState(script));
      return;
    }
    if (!active) return;

    let cancelled = false;
    const timers: ReturnType<typeof setTimeout>[] = [];

    const run = async () => {
      while (!cancelled) {
        setItems([]);
        await wait(400, timers);

        for (const step of script) {
          if (cancelled) return;

          if (step.kind === 'user') {
            await wait(550, timers);
            setItems((p) => [...p, { kind: 'user', text: step.text, shown: step.text }]);
          } else if (step.kind === 'tools') {
            await wait(650, timers);
            setItems((p) => [...p, { kind: 'tools', tools: step.tools, toolsDone: false }]);
            await wait(1000, timers);
            setItems((p) => p.map((it, i) => (i === p.length - 1 ? { ...it, toolsDone: true } : it)));
          } else if (step.kind === 'note') {
            await wait(500, timers);
            setItems((p) => [...p, { kind: 'note', label: step.label, text: '', shown: '', typing: true }]);
            await wait(450, timers);
            const text = step.text;
            for (let i = 1; i <= text.length; i++) {
              if (cancelled) return;
              setItems((p) =>
                p.map((it, idx) => (idx === p.length - 1 ? { ...it, shown: text.slice(0, i), typing: false } : it)),
              );
              await wait(14, timers);
            }
          } else {
            // bot
            await wait(550, timers);
            setItems((p) => [...p, { kind: 'bot', text: step.text, shown: '', typing: true, chips: step.chips }]);
            await wait(600, timers);
            const text = step.text;
            for (let i = 1; i <= text.length; i++) {
              if (cancelled) return;
              setItems((p) =>
                p.map((it, idx) => (idx === p.length - 1 ? { ...it, shown: text.slice(0, i), typing: false } : it)),
              );
              await wait(16, timers);
            }
          }
        }
        // 최종 결과를 충분히 읽을 수 있도록 길게 멈춘 뒤 초기화·반복
        await wait(7000, timers);
      }
    };

    run();
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [active, reduce, script]);

  return (
    <div className={`ai-chat ai-chat--${variant}`} ref={rootRef}>
      <div className="ai-chat__bar">
        <span className="ai-chat__dot" />
        <span className="ai-chat__dot" />
        <span className="ai-chat__dot" />
        <span className="ai-chat__title">{title}</span>
        <span className="ai-chat__live">
          <i /> LIVE
        </span>
      </div>

      <div className="ai-chat__body">
        <div className="ai-chat__fade" />
        {items.map((it, i) => (
          <ChatRow key={i} item={it} />
        ))}
      </div>

      {caption && <div className="ai-diagram__caption">{caption}</div>}
    </div>
  );
}

function ChatRow({ item }: { item: RenderItem }) {
  if (item.kind === 'user') {
    return (
      <div className="ai-chat__row is-user">
        <div className="ai-chat__bubble is-user">{item.shown}</div>
      </div>
    );
  }
  if (item.kind === 'tools') {
    return (
      <div className="ai-chat__row is-tools">
        {item.tools?.map((t) => (
          <span key={t} className={`ai-chat__tool ${item.toolsDone ? 'is-done' : ''}`}>
            <i className="ai-chat__tool-ic" />
            {t}
            <b>{item.toolsDone ? '✓' : '…'}</b>
          </span>
        ))}
      </div>
    );
  }
  if (item.kind === 'note') {
    return (
      <div className="ai-chat__row is-note">
        <span className="ai-chat__note-label">{item.label}</span>
        <span className="ai-chat__note-text">
          {item.shown}
          {item.typing && <span className="ai-chat__caret" />}
        </span>
      </div>
    );
  }
  // bot
  return (
    <div className="ai-chat__row is-bot">
      <div className="ai-chat__bubble is-bot">
        {item.typing && !item.shown ? (
          <span className="ai-chat__typing">
            <i />
            <i />
            <i />
          </span>
        ) : (
          <>
            {item.shown}
            {item.chips && item.shown === item.text && (
              <span className="ai-chat__chips">
                {item.chips.map((c) => (
                  <span key={c} className="ai-chat__chip">
                    {c}
                  </span>
                ))}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
